import dayjs from 'dayjs';
import { action, computed, observable, runInAction } from 'mobx';
import React from 'react';
import type { NavigateFunction } from 'react-router';
import type { ModalOkCancelProps } from '../components/ModalOkCancel/ModalOkCancel';
import { getAppConfig } from '../config/app';
import { MessageExitUpgrade, MessageSessionEnding } from '../layouts/BaseLayout/renderers';
import { storageService } from '../services/storage.service';
import { initializeUnleashClient } from '../services/unleash-feature-flags.service';
import { AppConfig } from '../types/app-config';
import {
  AgentData,
  AuditStatus,
  isUpgradeScheduled,
  PracticeConfig,
  UpgradeStatus,
} from '../types/practice-types';
import type { Sap } from '../types/users-types';
import { allowedUpgradeEventInterval } from '../utils/date-utils';
import EventIntervalsHandler from '../utils/event-intervals-handler';
import { isDefined, sortByField } from '../utils/object-utils';
import AbstractStore from './AbstractStore';
import { PRACTICE_PATH } from './constants';
import RootStore from './RootStore';

export const MODAL_ID = 'global-modal';

const LOGGING_OUT = 'LOGGING_OUT';

const TIME_INTERVAL_MINUTES = 30;
const TIMEOUT_SHIFT_MINUTES = 5;

export default class GlobalStore extends AbstractStore {
  @observable appConfig?: AppConfig;

  @observable _sapIdChanged = true;

  @observable selectedSap: Sap = { id: '', name: '' };

  @observable practiceSelectorDisabled = false;

  @observable isLoggedIn = false;

  @observable isLoading = false;

  @observable practice: PracticeConfig | undefined;

  @observable agentsData: AgentData[] = [];

  @observable eventIntervalsHandler: EventIntervalsHandler = new EventIntervalsHandler(
    [],
    TIME_INTERVAL_MINUTES,
  );

  @observable contactsChanged = false;

  @observable leftNavPanelShown = false;

  @observable globalModalProps: ModalOkCancelProps = {
    show: false,
    onOk: () => {},
    id: MODAL_ID,
    okCaption: '',
    exitOkAction: false,
  };

  @observable private _loggingOut = false;

  private upgradeTaskPopupTimeoutHandle: ReturnType<typeof setTimeout> | undefined = undefined;

  private upgradeTaskTimeoutHandle: ReturnType<typeof setTimeout> | undefined = undefined;

  public constructor(rootStore: RootStore) {
    super(rootStore);
    this._loggingOut = storageService.getItem(LOGGING_OUT, true) || false;
  }

  @computed
  public get loggingOut(): boolean {
    return this._loggingOut;
  }

  @computed
  public get userSaps(): Sap[] {
    return sortByField(this.root.apiStore.user?.saps || [], 'id');
  }

  @computed
  public get scheduleUpgradeTimeSet(): boolean {
    return (
      isDefined(this.practice?.scheduleUpgradeTimestamp) && isDefined(this.practice?.utcOffset)
    );
  }

  @computed
  public get upgradeScheduled(): boolean {
    return isUpgradeScheduled(this.practice);
  }

  @computed
  public get serverAgentsToUpgrade(): AgentData[] {
    return this.agentsData.filter(d => d.type === 'Server' && d.status === UpgradeStatus.UPGRADE);
  }

  @computed
  public get agentsReady(): boolean {
    if (this.agentsData.length === 0) return false;
    if (this.serverAgentsToUpgrade.length !== 1) return false;
    return this.agentsData.every(
      d => d.status !== UpgradeStatus.UPGRADE || d.auditStatus === AuditStatus.QUALIFIED,
    );
  }

  public get navigate(): NavigateFunction {
    return this._navigate;
  }

  public set loggingOut(value: boolean) {
    this._loggingOut = value;
    storageService.replace(LOGGING_OUT, value, true);
  }

  @action
  public changeSelectedSap(sapId: string, clear = true): void {
    if (this.selectedSap.id !== sapId) {
      this.sapIdChanged = true;
      const sap = this.userSaps.find(us => us.id === sapId);
      this.selectedSap = sap ? sap : { id: '', name: '' };
    } else if (clear) {
      this.selectedSap = { id: '', name: '' };
    }
  }

  @action
  public setLoading(value: boolean): void {
    this.isLoading = value;
  }

  @action
  public async fetchEventIntervals(): Promise<EventIntervalsHandler | undefined> {
    try {
      const allowedInterval = allowedUpgradeEventInterval();
      const events = await this.root.apiStore.findScheduledMaintenancesBetweenDates(
        allowedInterval.start,
        allowedInterval.end,
      );
      runInAction(
        () =>
          (this.eventIntervalsHandler = new EventIntervalsHandler(
            events.map(e => ({ start: e.startDateTime, end: e.endDateTime })),
            TIME_INTERVAL_MINUTES,
          )),
      );
      this.eventIntervalsHandler.setAllowedInterval(allowedInterval);
      return this.eventIntervalsHandler;
    } catch (e: any) {
      alert(e.toString());
    }
  }

  @action
  public async fetchPracticeData(withAgents = true, initialCheck = false): Promise<void> {
    const practice = await this.root.apiStore.getPractice(this.sapId, initialCheck);
    const agentsData = withAgents
      ? await this.root.apiStore.getCachedAgents(this.sapId)
      : this.agentsData;
    runInAction(() => {
      this.practice = practice;
      this.agentsData = agentsData;
      this.contactsChanged = false;
    });
  }

  @action
  public showOkCancelPopup(
    header: any,
    children: any,
    okCaption: string,
    cancelCaption: string,
    onOk: () => void | Promise<void>,
    onCancel?: () => void | Promise<void>,
    closeOnClickOutside = false,
    okAutoHide = true,
    exitOkAction = false,
  ): void {
    const act = async (ok: boolean): Promise<void> => {
      runInAction(() => (this.globalModalProps.disabled = true));
      if (ok) {
        await onOk();
      } else if (onCancel) {
        await onCancel();
      }
      runInAction(() => {
        this.globalModalProps.disabled = false;
        if (!ok || okAutoHide) this.globalModalProps.show = false;
      });
    };

    this.globalModalProps = {
      id: MODAL_ID,
      'data-testid': MODAL_ID,
      show: true,
      okCaption,
      cancelCaption,
      header,
      children,
      onOk: () => act(true),
      onCancel: () => act(false),
      exitOkAction,
      onClickOutside: closeOnClickOutside ? () => act(false) : undefined,
    };
  }

  @action
  public showOkPopup(header: any, children: any, onOk?: () => void, okCaption?: string): void {
    const act = (): void => {
      runInAction(() => (this.globalModalProps.show = false));
      onOk?.();
    };
    this.globalModalProps = {
      id: MODAL_ID,
      'data-testid': MODAL_ID,
      show: true,
      okCaption: okCaption || 'OK',
      header,
      children,
      onClickOutside: onOk ? () => act() : undefined,
      onOk: act,
      exitOkAction: true,
    };
  }

  public async init(navigate: NavigateFunction): Promise<void> {
    runInAction(() => (this._navigate = navigate));
    const appConfig = await getAppConfig();
    if (appConfig) {
      runInAction(() => (this.appConfig = appConfig));
      await initializeUnleashClient({
        appName: this.appConfig!.unleashFeatureFlagsAppName,
        clientKey: this.appConfig!.unleashFeatureFlagsClientKey,
        url: this.appConfig!.unleashFeatureFlagsUrl,
        context: { userId: this.root.apiStore.userEmail, sessionId: this.root.apiStore.userGuid },
        refreshInterval: 60,
      });
    }
  }

  public showOkCancelPopupAsync(
    header: any,
    children: any,
    okCaption: string,
    cancelCaption: string,
    onOk: () => void | Promise<void>,
    onCancel?: () => void | Promise<void>,
    closeOnClickOutside = false,
    okAutoHide = true,
    exitOkAction = false,
  ): Promise<void> {
    return new Promise(resolve =>
      this.showOkCancelPopup(
        header,
        children,
        okCaption,
        cancelCaption,
        async () => {
          await onOk?.();
          resolve();
        },
        async () => {
          await onCancel?.();
          resolve();
        },
        closeOnClickOutside,
        okAutoHide,
        exitOkAction,
      ),
    );
  }

  public showOkPopupAsync(
    header: any,
    children: any,
    onOk?: () => void,
    okCaption?: string,
  ): Promise<void> {
    return new Promise(resolve =>
      this.showOkPopup(
        header,
        children,
        () => {
          onOk?.();
          resolve();
        },
        okCaption,
      ),
    );
  }

  public showExitUpgradePopup(onOk: () => void): void {
    this.showOkCancelPopup(
      'Exit Upgrade?',
      React.createElement(MessageExitUpgrade),
      'Save and Exit',
      'Return to Upgrade',
      onOk,
    );
  }

  public showSessionEndingPopup(): void {
    this.showOkCancelPopup(
      'Session Ending',
      React.createElement(MessageSessionEnding),
      'Extend Session',
      'End Session',
      () => this.extendUpgradeTaskSessionLock(),
      () => this.navigate('/'),
    );
  }

  public setUpgradeTaskSessionEndTimeouts(): void {
    this.clearUpgradeTaskSessionEndTimeouts();
    if (this.practice?.isLocked && this.practice.lockedByEmail === this.root.apiStore.userEmail) {
      const endTime = dayjs(this.practice.lockEndTimestamp);
      let timeoutMs = endTime.add(-TIMEOUT_SHIFT_MINUTES, 'minutes').diff(dayjs(), 'ms');
      if (timeoutMs < 1000) timeoutMs = 1000;
      this.upgradeTaskPopupTimeoutHandle = setTimeout(
        () => this.showSessionEndingPopup(),
        timeoutMs,
      );
      this.upgradeTaskTimeoutHandle = setTimeout(
        () => this.navigate('/'),
        endTime.diff(dayjs(), 'ms'),
      );
      console.log(`Upgrade task session timeout is set to ${timeoutMs / 1000}s`);
    }
  }

  public clearUpgradeTaskSessionEndTimeouts(): void {
    if (this.upgradeTaskPopupTimeoutHandle) {
      clearTimeout(this.upgradeTaskPopupTimeoutHandle);
      this.upgradeTaskPopupTimeoutHandle = undefined;
      clearTimeout(this.upgradeTaskTimeoutHandle);
      this.upgradeTaskTimeoutHandle = undefined;
      console.log('Upgrade task session timeout is cleared');
    }
  }

  public async extendUpgradeTaskSessionLock(): Promise<void> {
    const practice = await this.root.apiStore.backendPost(PRACTICE_PATH, {
      action: 'EXTEND_SCHEDULE_UPGRADE_PROCESS',
      sapId: this.sapId,
    });
    runInAction(() => (this.practice = practice));
    this.setUpgradeTaskSessionEndTimeouts();
  }

  public async endUpgradeTaskSessionIfStarted(): Promise<void> {
    this.clearUpgradeTaskSessionEndTimeouts();
    if (this.selectedSap.id !== '' && this.practice?.isLocked) {
      console.log(`Unlocking upgrade task session for sapId=${this.selectedSap.id}`);
      await this.root.apiStore.backendPost(PRACTICE_PATH, {
        action: 'EXIT_SCHEDULE_UPGRADE_PROCESS',
        sapId: this.selectedSap.id,
      });
      runInAction(() => {
        this.isLoggedIn = false;
        this.globalModalProps.show = false;
      });
    }
  }

  public async signOut(): Promise<void> {
    this.loggingOut = true;
    await this.endUpgradeTaskSessionIfStarted();

    for (const storeName of Object.keys(this.root)) {
      const store = this.root[storeName] as AbstractStore;
      if (!(store instanceof GlobalStore)) await store.signOut();
    }
  }

  public onChangeSapId(): void {
    for (const storeName of Object.keys(this.root)) {
      const store = this.root[storeName] as AbstractStore;
      if (!(store instanceof GlobalStore)) store.onChangeSapId();
    }
  }

  private _navigate: NavigateFunction = () => {};
}
